/**
 * Market Management - add, correct and retire the markets prices are collected in.
 *
 * This page did not exist, and its absence was doing real damage. The endpoints
 * were there all along, but nothing drove them, so a market could only be added
 * by an engineer calling the API by hand. Two consequences followed.
 *
 * The first was silent bad data. The local government is optional on the
 * endpoint and nothing prompted for it, so MKT024 (Pambegua) sat for months
 * with an empty LGA while serving real prices. Here the field is required when
 * adding a market, so the same hole cannot be dug again, and any market still
 * missing one is called out at the top of the page rather than left to be
 * noticed by accident.
 *
 * The second was a dead end in the upload flow. When a spreadsheet carries a
 * market sheet we do not recognise, Data Upload tells the admin to add an
 * alias. That is right when the sheet is a new spelling of a market we already
 * have, and useless when it is a market we have never heard of, because there
 * was nowhere to create one. That page now links here as well.
 *
 * Deleting is deliberately the last resort: the server refuses to delete a
 * market that any price row references, so retiring is the normal way to take
 * one out of circulation. Retired markets already disappear from the public
 * site and the API while their history keeps its subject.
 */

import React, { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
  Box, Card, Typography, Button, Chip, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TableSortLabel, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, IconButton, Snackbar,
  Alert as MuiAlert, CircularProgress, Tooltip, InputAdornment, TablePagination,
  Tabs, Tab,
} from '@mui/material';
import { Icon } from '@iconify/react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { PageHeader, StatCard, EmptyState, ConfirmDialog } from '../../components/common';
import { useThemeMode, MONO } from '../../context/ThemeContext';
import { useRecipes } from '../../theme/recipes';
import { fmtDate, fmtNumber } from '../../utils/format';
import { getStates, getMarkets, getMarketCoverage } from '../../services/catalog';
import {
  createMarket, updateMarket, deleteMarket,
  createState, updateState, deleteState,
} from '../../services/admin';

const ROWS_PER_PAGE = [10, 25, 50];

const EMPTY_FORM = { name: '', stateCode: '', lga: '', lat: '', lng: '', status: 'active' };
const EMPTY_STATE_FORM = { code: '', name: '', shortName: '', status: 'active' };

/** Latitude and longitude are optional, but a half-entered pair is a mistake. */
function coordProblem(form) {
  const has = (v) => String(v ?? '').trim() !== '';
  if (has(form.lat) !== has(form.lng)) {
    return 'Give both a latitude and a longitude, or neither.';
  }
  if (has(form.lat)) {
    const lat = Number(form.lat);
    const lng = Number(form.lng);
    if (Number.isNaN(lat) || Number.isNaN(lng)) return 'Coordinates must be numbers.';
    // Nigeria sits roughly between 4 and 14 north, 2 and 15 east. A wider box
    // than that is almost certainly a transposed pair or a typed minus sign.
    if (lat < 4 || lat > 14) return 'That latitude is outside Nigeria. Did the pair get swapped?';
    if (lng < 2 || lng > 15) return 'That longitude is outside Nigeria. Did the pair get swapped?';
  }
  return '';
}

function MarketManagement() {
  const { palette } = useThemeMode();
  const recipes = useRecipes();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');
  const [stateFilter, setStateFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('active');
  const [sortBy, setSortBy] = useState('id');
  const [sortDir, setSortDir] = useState('asc');
  const [page, setPage] = useState(0);
  const [perPage, setPerPage] = useState(25);

  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);   // null while adding
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formError, setFormError] = useState('');

  const [tab, setTab] = useState(0);

  const [stateForm, setStateForm] = useState(EMPTY_STATE_FORM);
  const [editingState, setEditingState] = useState(null);   // null while adding
  const [stateDialogOpen, setStateDialogOpen] = useState(false);
  const [stateError, setStateError] = useState('');
  const [stateToDelete, setStateToDelete] = useState(null);

  const [toRetire, setToRetire] = useState(null);
  const [toDelete, setToDelete] = useState(null);
  const [toast, setToast] = useState(null);

  const statesQuery = useQuery({ queryKey: ['states'], queryFn: getStates });
  const marketsQuery = useQuery({ queryKey: ['admin-markets'], queryFn: () => getMarkets() });
  const coverageQuery = useQuery({ queryKey: ['market-coverage'], queryFn: getMarketCoverage });

  const states = statesQuery.data || [];
  const markets = useMemo(() => marketsQuery.data || [], [marketsQuery.data]);
  // Memoised because `|| {}` would hand the sort/filter memo a brand new object
  // on every render, so it would recompute every time and never actually memo.
  const coverage = useMemo(() => coverageQuery.data || {}, [coverageQuery.data]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['admin-markets'] });
    queryClient.invalidateQueries({ queryKey: ['market-coverage'] });
  };

  const saveMutation = useMutation({
    mutationFn: () => {
      const body = {
        name: form.name.trim(),
        stateCode: form.stateCode,
        lga: form.lga.trim(),
        lat: String(form.lat).trim() === '' ? null : Number(form.lat),
        lng: String(form.lng).trim() === '' ? null : Number(form.lng),
        status: form.status,
      };
      return editingId ? updateMarket(editingId, body) : createMarket(body);
    },
    onSuccess: (saved) => {
      invalidate();
      setDialogOpen(false);
      setToast({
        severity: 'success',
        msg: editingId
          ? `${saved?.name || form.name} updated.`
          : `${saved?.name || form.name} added as ${saved?.id || 'a new market'}.`,
      });
    },
    onError: (e) => setFormError(e?.message || 'Could not save this market.'),
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, status }) => updateMarket(id, { status }),
    onSuccess: (_r, vars) => {
      invalidate();
      setToRetire(null);
      setToast({
        severity: 'success',
        msg: vars.status === 'retired'
          ? 'Retired. It disappears from the public site and the API; its history is kept.'
          : 'Restored. It appears on the public site and the API again.',
      });
    },
    onError: (e) => {
      setToRetire(null);
      setToast({ severity: 'error', msg: e?.message || 'Could not change the status.' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => deleteMarket(id),
    onSuccess: () => {
      invalidate();
      setToDelete(null);
      setToast({ severity: 'success', msg: 'Market deleted.' });
    },
    onError: (e) => {
      setToDelete(null);
      // The server refuses when price rows reference it, and says how many.
      setToast({ severity: 'error', msg: e?.message || 'Could not delete this market.' });
    },
  });

  const invalidateStates = () => {
    queryClient.invalidateQueries({ queryKey: ['states'] });
    queryClient.invalidateQueries({ queryKey: ['admin-markets'] });
  };

  const saveStateMutation = useMutation({
    mutationFn: () => {
      const body = {
        name: stateForm.name.trim(),
        shortName: stateForm.shortName.trim(),
        status: stateForm.status,
      };
      return editingState
        ? updateState(editingState, body)
        : createState({ ...body, code: stateForm.code.trim().toUpperCase() });
    },
    onSuccess: (saved) => {
      invalidateStates();
      setStateDialogOpen(false);
      setToast({
        severity: 'success',
        msg: editingState
          ? `${saved?.name || stateForm.name} updated.`
          : `${saved?.name || stateForm.name} added. You can now put markets in it.`,
      });
    },
    onError: (e) => setStateError(e?.message || 'Could not save this state.'),
  });

  const deleteStateMutation = useMutation({
    mutationFn: (code) => deleteState(code),
    onSuccess: () => {
      invalidateStates();
      setStateToDelete(null);
      setToast({ severity: 'success', msg: 'State deleted.' });
    },
    onError: (e) => {
      setStateToDelete(null);
      // The server refuses while markets are in it, and says how many.
      setToast({ severity: 'error', msg: e?.message || 'Could not delete this state.' });
    },
  });

  const openAddState = () => {
    setStateForm(EMPTY_STATE_FORM);
    setEditingState(null);
    setStateError('');
    setStateDialogOpen(true);
  };

  const openEditState = (st) => {
    setStateForm({
      code: st.code,
      name: st.name || '',
      shortName: st.shortName || st.short_name || '',
      status: st.status || 'active',
    });
    setEditingState(st.code);
    setStateError('');
    setStateDialogOpen(true);
  };

  const setStateField = (k) => (e) => setStateForm((f) => ({ ...f, [k]: e.target.value }));

  // How many markets sit in each state, so the page can say why one cannot be
  // deleted before the server has to.
  const marketsPerState = useMemo(() => {
    const out = {};
    markets.forEach((m) => { out[m.stateCode] = (out[m.stateCode] || 0) + 1; });
    return out;
  }, [markets]);

  const openAdd = () => {
    setForm(EMPTY_FORM);
    setEditingId(null);
    setFormError('');
    setDialogOpen(true);
  };

  const openEdit = (m) => {
    setForm({
      name: m.name || '',
      stateCode: m.stateCode || '',
      lga: m.lga || '',
      lat: m.lat ?? '',
      lng: m.lng ?? '',
      status: m.status || 'active',
    });
    setEditingId(m.id);
    setFormError('');
    setDialogOpen(true);
  };

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const coordError = coordProblem(form);
  const canSave =
    form.name.trim() !== '' && form.stateCode !== '' && form.lga.trim() !== '' && !coordError;

  // Markets carrying prices but missing a local government. Surfaced rather
  // than left to be found by accident, which is how Pambegua went unnoticed.
  const incomplete = useMemo(
    () => markets.filter((m) => !m.lga || String(m.lga).trim() === ''),
    [markets],
  );

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = markets.filter((m) => {
      if (statusFilter !== 'all' && m.status !== statusFilter) return false;
      if (stateFilter !== 'all' && m.stateCode !== stateFilter) return false;
      if (!q) return true;
      return [m.id, m.name, m.lga, m.state].some((v) => String(v || '').toLowerCase().includes(q));
    });
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      let av;
      let bv;
      if (sortBy === 'records') {
        av = coverage[a.id]?.records ?? -1;
        bv = coverage[b.id]?.records ?? -1;
      } else if (sortBy === 'latest') {
        av = coverage[a.id]?.latestDate || '';
        bv = coverage[b.id]?.latestDate || '';
      } else {
        av = String(a[sortBy] ?? '').toLowerCase();
        bv = String(b[sortBy] ?? '').toLowerCase();
      }
      if (av < bv) return -1 * dir;
      if (av > bv) return 1 * dir;
      return 0;
    });
  }, [markets, coverage, search, stateFilter, statusFilter, sortBy, sortDir]);

  const paged = visible.slice(page * perPage, page * perPage + perPage);

  const sortHandler = (col) => () => {
    if (sortBy === col) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortBy(col); setSortDir('asc'); }
    setPage(0);
  };

  const activeCount = markets.filter((m) => m.status === 'active').length;
  const surveyed = markets.filter((m) => coverage[m.id]?.records).length;
  const loading = marketsQuery.isLoading || statesQuery.isLoading;

  const headCell = (id, label, align = 'left') => (
    <TableCell sx={recipes.headCellSx} align={align}>
      <TableSortLabel
        active={sortBy === id}
        direction={sortBy === id ? sortDir : 'asc'}
        onClick={sortHandler(id)}
      >
        {label}
      </TableSortLabel>
    </TableCell>
  );

  return (
    <Box>
      <PageHeader
        eyebrow="Admin"
        title="Market Management"
        subtitle="The markets prices are collected in, and which of them are actually reporting"
        breadcrumbs={[{ label: 'Home', path: '/' }, { label: 'Market Management' }]}
        actions={
          <Button
            variant="contained"
            size="small"
            disableElevation
            onClick={tab === 0 ? openAdd : openAddState}
            startIcon={<Icon icon="ph:plus" width={15} height={15} />}
            sx={{ ...recipes.primaryBtnSx, textTransform: 'none' }}
          >
            {tab === 0 ? 'Add market' : 'Add state'}
          </Button>
        }
      />

      <Tabs
        value={tab}
        onChange={(_e, v) => setTab(v)}
        sx={{
          mb: 3, borderBottom: `1px solid ${palette.divider}`,
          '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, minHeight: 44 },
          ...recipes.tabsSx,
        }}
      >
        <Tab icon={<Icon icon="ph:storefront" width={16} height={16} />} iconPosition="start" label="Markets" />
        <Tab icon={<Icon icon="ph:map-pin-area" width={16} height={16} />} iconPosition="start" label="States" />
      </Tabs>

      {/* A market with prices but no local government is incomplete data we
          are already publishing, so it leads rather than hides. */}
      {tab === 0 && incomplete.length > 0 && (
        <MuiAlert
          severity="warning"
          sx={{ mb: 2.5 }}
          action={
            incomplete.length === 1 ? (
              <Button
                size="small"
                onClick={() => openEdit(incomplete[0])}
                sx={{ textTransform: 'none', fontWeight: 600, whiteSpace: 'nowrap' }}
              >
                Fix it
              </Button>
            ) : null
          }
        >
          {incomplete.length === 1
            ? `${incomplete[0].name} has no local government recorded.`
            : `${incomplete.length} markets have no local government recorded: ${incomplete.map((m) => m.name).join(', ')}.`}
          {' '}Anywhere the site prints "Market, LGA, State" it renders with a gap.
        </MuiAlert>
      )}

      {tab === 0 && (
      <>
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(4, 1fr)' }, gap: 2.5, mb: 3 }}>
        <StatCard
          label="Markets"
          value={fmtNumber(markets.length)}
          sub={`${activeCount} active, ${markets.length - activeCount} retired`}
          icon={<Icon icon="ph:storefront" width={20} height={20} />}
        />
        <StatCard
          label="Reporting prices"
          value={fmtNumber(surveyed)}
          sub={surveyed < activeCount ? `${activeCount - surveyed} active with no data yet` : 'every active market'}
          icon={<Icon icon="ph:chart-line-up" width={20} height={20} />}
          tone={surveyed < activeCount ? 'warning' : 'accent'}
        />
        <StatCard
          label="States covered"
          value={fmtNumber(new Set(markets.filter((m) => m.status === 'active').map((m) => m.stateCode)).size)}
          sub={`of ${states.length} on file`}
          icon={<Icon icon="ph:map-pin-area" width={20} height={20} />}
        />
        <StatCard
          label="Missing an LGA"
          value={fmtNumber(incomplete.length)}
          sub={incomplete.length ? 'incomplete records' : 'every market complete'}
          icon={<Icon icon="ph:warning-circle" width={20} height={20} />}
          tone={incomplete.length ? 'danger' : 'accent'}
        />
      </Box>

      <Card>
        <Box
          sx={{
            px: 2.5, py: 1.75, borderBottom: `1px solid ${palette.divider}`,
            display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center',
          }}
        >
          <TextField
            size="small"
            placeholder="Search name, LGA or id"
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(0); }}
            inputProps={{ 'aria-label': 'Search markets' }}
            InputProps={{
              sx: recipes.controlSx,
              startAdornment: (
                <InputAdornment position="start">
                  <Icon icon="ph:magnifying-glass" width={16} height={16} color={palette.textMuted} />
                </InputAdornment>
              ),
            }}
            sx={{ minWidth: 240, flex: 1 }}
          />
          <TextField
            select size="small" label="State"
            value={stateFilter}
            onChange={(e) => { setStateFilter(e.target.value); setPage(0); }}
            InputLabelProps={{ sx: recipes.labelSx }}
            InputProps={{ sx: recipes.controlSx }}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="all">All states</MenuItem>
            {states.map((s) => (
              <MenuItem key={s.code} value={s.code}>{s.name || s.shortName}</MenuItem>
            ))}
          </TextField>
          <TextField
            select size="small" label="Status"
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}
            InputLabelProps={{ sx: recipes.labelSx }}
            InputProps={{ sx: recipes.controlSx }}
            sx={{ minWidth: 140 }}
          >
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="retired">Retired</MenuItem>
            <MenuItem value="all">All</MenuItem>
          </TextField>
        </Box>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={26} sx={{ color: palette.accent }} />
          </Box>
        )}

        {marketsQuery.error && (
          <Box sx={{ p: 3 }}>
            <MuiAlert severity="error">{marketsQuery.error?.message || 'Could not load markets.'}</MuiAlert>
          </Box>
        )}

        {!loading && !marketsQuery.error && visible.length === 0 && (
          <EmptyState
            icon={<Icon icon="ph:storefront" width={24} height={24} />}
            title={markets.length === 0 ? 'No markets yet' : 'Nothing matches those filters'}
            description={
              markets.length === 0
                ? 'Add the markets your surveyors collect prices in. Uploads can only match sheets to markets that exist here.'
                : 'Try a different state, status or search term.'
            }
            actionLabel={markets.length === 0 ? 'Add market' : undefined}
            onAction={markets.length === 0 ? openAdd : undefined}
          />
        )}

        {!loading && !marketsQuery.error && visible.length > 0 && (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    {headCell('id', 'ID')}
                    {headCell('name', 'Market')}
                    {headCell('state', 'State')}
                    {headCell('lga', 'LGA')}
                    {headCell('records', 'Price rows', 'right')}
                    {headCell('latest', 'Latest')}
                    <TableCell sx={recipes.headCellSx}>Status</TableCell>
                    <TableCell sx={recipes.headCellSx} align="right">Manage</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paged.map((m) => {
                    const cov = coverage[m.id];
                    const retired = m.status === 'retired';
                    const noLga = !m.lga || String(m.lga).trim() === '';
                    return (
                      <TableRow key={m.id} hover sx={{ ...recipes.bodyRowSx, opacity: retired ? 0.6 : 1 }}>
                        <TableCell sx={{ fontFamily: MONO, fontSize: '0.78rem', color: palette.textSecondary }}>
                          {m.id}
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ fontSize: '0.85rem', fontWeight: 600, color: palette.text }}>
                            {m.name}
                          </Typography>
                          {m.lat != null && m.lng != null && (
                            <Typography sx={{ fontFamily: MONO, fontSize: '0.68rem', color: palette.textMuted }}>
                              {Number(m.lat).toFixed(4)}, {Number(m.lng).toFixed(4)}
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.82rem', color: palette.textSecondary }}>{m.state}</TableCell>
                        <TableCell>
                          {noLga ? (
                            <Chip
                              label="Missing"
                              size="small"
                              onClick={() => openEdit(m)}
                              sx={{
                                height: 19, fontSize: '0.62rem', fontWeight: 700, cursor: 'pointer',
                                bgcolor: palette.dangerBg, color: palette.danger,
                              }}
                            />
                          ) : (
                            <Typography sx={{ fontSize: '0.82rem', color: palette.textSecondary }}>{m.lga}</Typography>
                          )}
                        </TableCell>
                        <TableCell align="right" sx={{ fontFamily: MONO, fontSize: '0.8rem' }}>
                          {cov?.records ? fmtNumber(cov.records) : (
                            <Typography component="span" sx={{ fontSize: '0.78rem', color: palette.textMuted }}>
                              none
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.78rem', color: cov?.latestDate ? palette.textSecondary : palette.textMuted }}>
                          {cov?.latestDate ? fmtDate(cov.latestDate) : '-'}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={retired ? 'RETIRED' : 'ACTIVE'}
                            size="small"
                            sx={{
                              height: 19, fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.05em',
                              bgcolor: retired ? palette.surface : palette.accentBg,
                              color: retired ? palette.textMuted : palette.accent,
                              border: retired ? `1px solid ${palette.cardBorder}` : 'none',
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', gap: 0.25, justifyContent: 'flex-end' }}>
                            <Tooltip title="Edit this market">
                              <IconButton
                                size="small"
                                aria-label={`Edit ${m.name}`}
                                onClick={() => openEdit(m)}
                                sx={{ color: palette.textMuted, '&:hover': { color: palette.accent, bgcolor: palette.accentBg } }}
                              >
                                <Icon icon="ph:pencil-simple" width={16} height={16} />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={retired ? 'Put this market back into circulation' : 'Retire this market'}>
                              <IconButton
                                size="small"
                                aria-label={retired ? `Restore ${m.name}` : `Retire ${m.name}`}
                                onClick={() =>
                                  (retired
                                    ? statusMutation.mutate({ id: m.id, status: 'active' })
                                    : setToRetire(m))}
                                sx={{ color: palette.textMuted, '&:hover': { color: palette.warning, bgcolor: palette.warningBg } }}
                              >
                                <Icon icon={retired ? 'ph:arrow-counter-clockwise' : 'ph:archive'} width={16} height={16} />
                              </IconButton>
                            </Tooltip>
                            {/* Only offered where it can succeed: the server
                                refuses to delete a market with price rows. */}
                            {!cov?.records && (
                              <Tooltip title="Delete this market">
                                <IconButton
                                  size="small"
                                  aria-label={`Delete ${m.name}`}
                                  onClick={() => setToDelete(m)}
                                  sx={{ color: palette.textMuted, '&:hover': { color: palette.danger, bgcolor: palette.dangerBg } }}
                                >
                                  <Icon icon="ph:trash" width={16} height={16} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={visible.length}
              page={page}
              onPageChange={(_e, p) => setPage(p)}
              rowsPerPage={perPage}
              onRowsPerPageChange={(e) => { setPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={ROWS_PER_PAGE}
              sx={recipes.paginationSx}
            />
          </>
        )}
      </Card>

      <Box sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start', mt: 2.5, px: 0.5 }}>
        <Icon icon="ph:info" width={16} height={16} color={palette.textMuted} style={{ marginTop: 2, flexShrink: 0 }} />
        <Typography sx={{ fontSize: '0.78rem', color: palette.textSecondary, lineHeight: 1.6, maxWidth: 800 }}>
          An upload can only match a sheet to a market that exists here. If a spreadsheet
          names a market you have never collected before, add it on this page; if it is a
          new spelling of one you already have, add an alias on{' '}
          <Box component={RouterLink} to="/admin/aliases" sx={{ color: palette.accent, fontWeight: 600, textDecoration: 'none' }}>
            Alias Management
          </Box>{' '}
          instead. Retiring takes a market off the public site and out of the API while
          keeping its history; deleting is only possible before any prices reference it.
        </Typography>
      </Box>
      </>
      )}

      {/* ------------------------------ States ------------------------------ */}
      {tab === 1 && (
        <>
          <Typography sx={{ fontSize: '0.88rem', color: palette.textSecondary, mb: 2, maxWidth: 800, lineHeight: 1.6 }}>
            The states markets can be placed in. Until this page existed these could only be
            added by writing a database migration, so expanding coverage needed an engineer.
            A state's code is its permanent identifier and markets are joined on it, so it
            cannot be edited once created.
          </Typography>

          {statesQuery.isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
              <CircularProgress size={26} sx={{ color: palette.accent }} />
            </Box>
          ) : states.length === 0 ? (
            <Card sx={{ p: 2.5 }}>
              <EmptyState
                icon={<Icon icon="ph:map-pin-area" width={24} height={24} />}
                title="No states yet"
                description="Add a state before adding markets in it."
                actionLabel="Add state"
                onAction={openAddState}
              />
            </Card>
          ) : (
            <TableContainer component={Card}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={recipes.headCellSx}>Code</TableCell>
                    <TableCell sx={recipes.headCellSx}>Name</TableCell>
                    <TableCell sx={recipes.headCellSx}>Short name</TableCell>
                    <TableCell sx={recipes.headCellSx} align="right">Markets</TableCell>
                    <TableCell sx={recipes.headCellSx}>Status</TableCell>
                    <TableCell sx={recipes.headCellSx} align="right">Manage</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {states.map((st) => {
                    const inUse = marketsPerState[st.code] || 0;
                    const retired = st.status === 'retired';
                    return (
                      <TableRow key={st.code} hover sx={{ ...recipes.bodyRowSx, opacity: retired ? 0.6 : 1 }}>
                        <TableCell sx={{ fontFamily: MONO, fontSize: '0.8rem', fontWeight: 700, color: palette.text }}>
                          {st.code}
                        </TableCell>
                        <TableCell sx={{ fontSize: '0.85rem', fontWeight: 600 }}>{st.name}</TableCell>
                        <TableCell sx={{ fontSize: '0.82rem', color: palette.textSecondary }}>
                          {st.shortName || st.short_name || '-'}
                        </TableCell>
                        <TableCell align="right" sx={{ fontFamily: MONO, fontSize: '0.8rem' }}>
                          {inUse || (
                            <Typography component="span" sx={{ fontSize: '0.78rem', color: palette.textMuted }}>
                              none
                            </Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={retired ? 'RETIRED' : 'ACTIVE'}
                            size="small"
                            sx={{
                              height: 19, fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.05em',
                              bgcolor: retired ? palette.surface : palette.accentBg,
                              color: retired ? palette.textMuted : palette.accent,
                              border: retired ? `1px solid ${palette.cardBorder}` : 'none',
                            }}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', gap: 0.25, justifyContent: 'flex-end' }}>
                            <Tooltip title="Edit this state">
                              <IconButton
                                size="small"
                                aria-label={`Edit ${st.name}`}
                                onClick={() => openEditState(st)}
                                sx={{ color: palette.textMuted, '&:hover': { color: palette.accent, bgcolor: palette.accentBg } }}
                              >
                                <Icon icon="ph:pencil-simple" width={16} height={16} />
                              </IconButton>
                            </Tooltip>
                            {/* Only where it can succeed: the server refuses
                                while any market is in this state. */}
                            {inUse === 0 && (
                              <Tooltip title="Delete this state">
                                <IconButton
                                  size="small"
                                  aria-label={`Delete ${st.name}`}
                                  onClick={() => setStateToDelete(st)}
                                  sx={{ color: palette.textMuted, '&:hover': { color: palette.danger, bgcolor: palette.dangerBg } }}
                                >
                                  <Icon icon="ph:trash" width={16} height={16} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </>
      )}

      {/* ---- Add / edit a state ---- */}
      <Dialog
        open={stateDialogOpen}
        onClose={() => setStateDialogOpen(false)}
        fullWidth
        PaperProps={{ sx: { ...recipes.dialogPaperProps.sx, maxWidth: 460, width: '100%' } }}
      >
        <DialogTitle sx={{ pb: 1 }}>
          {editingState ? `Edit ${stateForm.name || 'state'}` : 'Add a state'}
        </DialogTitle>
        <DialogContent dividers>
          {stateError && <MuiAlert severity="error" sx={{ mb: 2 }}>{stateError}</MuiAlert>}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}>
            <TextField
              label="Code" size="small" fullWidth
              autoFocus={!editingState}
              disabled={!!editingState}
              value={stateForm.code}
              onChange={setStateField('code')}
              helperText={editingState
                ? 'The code cannot change: markets are joined on it'
                : 'Two to four letters, like KN. Used as the permanent identifier.'}
              inputProps={{ maxLength: 4, style: { textTransform: 'uppercase' } }}
              InputLabelProps={{ sx: recipes.labelSx }}
              InputProps={{ sx: recipes.controlSx }}
            />
            <TextField
              label="Full name" size="small" fullWidth
              autoFocus={!!editingState}
              value={stateForm.name}
              onChange={setStateField('name')}
              helperText="As it should read on the site, like Kano State"
              inputProps={{ maxLength: 80 }}
              InputLabelProps={{ sx: recipes.labelSx }}
              InputProps={{ sx: recipes.controlSx }}
            />
            <TextField
              label="Short name" size="small" fullWidth
              value={stateForm.shortName}
              onChange={setStateField('shortName')}
              helperText="What tables and filters show, like Kano"
              inputProps={{ maxLength: 40 }}
              InputLabelProps={{ sx: recipes.labelSx }}
              InputProps={{ sx: recipes.controlSx }}
            />
            {editingState && (
              <TextField
                select label="Status" size="small" fullWidth
                value={stateForm.status}
                onChange={setStateField('status')}
                InputLabelProps={{ sx: recipes.labelSx }}
                InputProps={{ sx: recipes.controlSx }}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="retired">Retired</MenuItem>
              </TextField>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setStateDialogOpen(false)} sx={{ color: palette.textMuted, textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disableElevation
            onClick={() => { setStateError(''); saveStateMutation.mutate(); }}
            disabled={
              saveStateMutation.isPending
              || stateForm.name.trim() === ''
              || stateForm.shortName.trim() === ''
              || (!editingState && stateForm.code.trim().length < 2)
            }
            sx={{ ...recipes.primaryBtnSx, textTransform: 'none' }}
          >
            {saveStateMutation.isPending ? 'Saving…' : (editingState ? 'Save changes' : 'Add state')}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!stateToDelete}
        onClose={() => setStateToDelete(null)}
        onConfirm={() => deleteStateMutation.mutate(stateToDelete.code)}
        title={`Delete ${stateToDelete?.name || 'this state'}?`}
        description="This removes the state permanently. It is only possible because no market is in it; retire it instead if you may want it back."
        confirmLabel="Delete permanently"
        tone="danger"
        icon="ph:trash"
      />

      {/* ---- Add / edit ---- */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        fullWidth
        PaperProps={{ sx: { ...recipes.dialogPaperProps.sx, maxWidth: 520, width: '100%' } }}
      >
        <DialogTitle sx={{ pb: 1 }}>{editingId ? `Edit ${form.name || 'market'}` : 'Add a market'}</DialogTitle>
        <DialogContent dividers>
          {formError && <MuiAlert severity="error" sx={{ mb: 2 }}>{formError}</MuiAlert>}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}>
            <TextField
              label="Market name" size="small" fullWidth autoFocus
              value={form.name}
              onChange={setField('name')}
              helperText="As your surveyors name it, and as the upload sheet is titled"
              inputProps={{ maxLength: 120 }}
              InputLabelProps={{ sx: recipes.labelSx }}
              InputProps={{ sx: recipes.controlSx }}
            />
            <TextField
              select label="State" size="small" fullWidth
              value={form.stateCode}
              onChange={setField('stateCode')}
              InputLabelProps={{ sx: recipes.labelSx }}
              InputProps={{ sx: recipes.controlSx }}
            >
              {states.map((s) => (
                <MenuItem key={s.code} value={s.code}>{s.name || s.shortName}</MenuItem>
              ))}
            </TextField>
            <TextField
              label="Local government" size="small" fullWidth required
              value={form.lga}
              onChange={setField('lga')}
              helperText="Required here even though the API allows it to be empty, because an empty one has already shipped incomplete data once"
              inputProps={{ maxLength: 120 }}
              InputLabelProps={{ sx: recipes.labelSx }}
              InputProps={{ sx: recipes.controlSx }}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                label="Latitude" size="small" fullWidth
                value={form.lat}
                onChange={setField('lat')}
                placeholder="11.3833"
                InputLabelProps={{ sx: recipes.labelSx }}
                InputProps={{ sx: recipes.controlSx }}
              />
              <TextField
                label="Longitude" size="small" fullWidth
                value={form.lng}
                onChange={setField('lng')}
                placeholder="7.3833"
                InputLabelProps={{ sx: recipes.labelSx }}
                InputProps={{ sx: recipes.controlSx }}
              />
            </Box>
            {coordError ? (
              <MuiAlert severity="warning" sx={{ py: 0.25 }}>{coordError}</MuiAlert>
            ) : (
              <Typography sx={{ fontSize: '0.75rem', color: palette.textMuted, mt: -1 }}>
                Optional. Only used to place the market on the map.
              </Typography>
            )}
            {editingId && (
              <TextField
                select label="Status" size="small" fullWidth
                value={form.status}
                onChange={setField('status')}
                helperText="Retired markets keep their history but leave the public site and the API"
                InputLabelProps={{ sx: recipes.labelSx }}
                InputProps={{ sx: recipes.controlSx }}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="retired">Retired</MenuItem>
              </TextField>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setDialogOpen(false)} sx={{ color: palette.textMuted, textTransform: 'none' }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disableElevation
            onClick={() => { setFormError(''); saveMutation.mutate(); }}
            disabled={saveMutation.isPending || !canSave}
            sx={{ ...recipes.primaryBtnSx, textTransform: 'none' }}
          >
            {saveMutation.isPending ? 'Saving…' : (editingId ? 'Save changes' : 'Add market')}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={!!toRetire}
        onClose={() => setToRetire(null)}
        onConfirm={() => statusMutation.mutate({ id: toRetire.id, status: 'retired' })}
        title={`Retire ${toRetire?.name || 'this market'}?`}
        description={
          coverage[toRetire?.id]?.records
            ? `It leaves the public site and the API immediately. Its ${fmtNumber(coverage[toRetire.id].records)} price rows are kept and stay attributed to it, and uploads will refuse new rows for it until you restore it.`
            : 'It leaves the public site and the API immediately, and uploads will refuse rows for it until you restore it. You can undo this at any time.'
        }
        confirmLabel="Retire market"
        tone="danger"
        icon="ph:archive"
      />

      <ConfirmDialog
        open={!!toDelete}
        onClose={() => setToDelete(null)}
        onConfirm={() => deleteMutation.mutate(toDelete.id)}
        title={`Delete ${toDelete?.name || 'this market'}?`}
        description="This removes the market permanently and cannot be undone. It is only possible because no price row references it; retire it instead if you may want it back."
        confirmLabel="Delete permanently"
        tone="danger"
        icon="ph:trash"
      />

      <Snackbar
        open={!!toast}
        autoHideDuration={5000}
        onClose={() => setToast(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        {toast && <MuiAlert severity={toast.severity} onClose={() => setToast(null)}>{toast.msg}</MuiAlert>}
      </Snackbar>
    </Box>
  );
}

export default MarketManagement;
